import { Component, EventEmitter, Input, Output } from "@angular/core";
import { EstadoPeriodontograma } from "app/models/periodontograma.interface";
import { EstadoOdontograma } from "app/models/odontograma.interface";
import { PeriodontogramaConfigService } from "app/services/periodontograma-config.service";
import { PRONOSTICOS } from "app/shared/periodontograma/catalogo-periodontograma";
import { calcularIndices, IndicesPerio } from "app/shared/periodontograma/indices-periodontograma";

type CaraPerio = "vestibular" | "palatino";

const PERM_SUP = ["18","17","16","15","14","13","12","11","21","22","23","24","25","26","27","28"];
const PERM_INF = ["48","47","46","45","44","43","42","41","31","32","33","34","35","36","37","38"];
const TEMP_SUP = ["55","54","53","52","51","61","62","63","64","65"];
const TEMP_INF = ["85","84","83","82","81","71","72","73","74","75"];

const TOOTH_AUSENTE = ["ausente", "extraccion_indicada", "no_erupcionado"];

@Component({
  selector: "app-periodontograma-chart",
  templateUrl: "./periodontograma-chart.component.html",
  styleUrls: ["./periodontograma-chart.component.scss"],
})
export class PeriodontogramaChartComponent {
  @Input() estado: EstadoPeriodontograma = {};
  @Input() odontogramaEstado: EstadoOdontograma = {};
  @Input() mode: "edit" | "readonly" = "edit";
  @Input() denticion: "adulto" | "nino" | "mixta" = "adulto";

  @Output() dienteClick = new EventEmitter<{ fdi: string }>();

  caras: CaraPerio[] = ["vestibular", "palatino"];
  pronosticos = PRONOSTICOS;

  constructor(private configService: PeriodontogramaConfigService) {}

  get filas(): string[][] {
    if (this.denticion === "nino") return [TEMP_SUP, TEMP_INF];
    if (this.denticion === "mixta") return [PERM_SUP, TEMP_SUP, TEMP_INF, PERM_INF];
    return [PERM_SUP, PERM_INF];
  }

  presente(fdi: string): boolean {
    const odo = this.odontogramaEstado?.[fdi];
    if (odo?.tooth && TOOTH_AUSENTE.includes(odo.tooth)) return false;
    const e = this.estado[fdi];
    return e ? e.presente !== false : true;
  }

  sondaje(fdi: string, cara: CaraPerio, i: number): number | null {
    return this.estado[fdi]?.[cara]?.sondaje?.[i] ?? null;
  }

  margen(fdi: string, cara: CaraPerio, i: number): number | null {
    return this.estado[fdi]?.[cara]?.margen?.[i] ?? null;
  }

  nic(fdi: string, cara: CaraPerio, i: number): number | null {
    const s = this.sondaje(fdi, cara, i);
    const m = this.margen(fdi, cara, i);
    if (s === null && m === null) return null;
    return (s ?? 0) + (m ?? 0);
  }

  sangrado(fdi: string, cara: CaraPerio, i: number): boolean {
    return !!this.estado[fdi]?.[cara]?.sangrado?.[i];
  }

  movilidad(fdi: string): number | null {
    return this.estado[fdi]?.movilidad ?? null;
  }

  furca(fdi: string): string | null {
    return this.estado[fdi]?.furca ?? null;
  }

  implante(fdi: string): boolean {
    return !!this.estado[fdi]?.implante;
  }

  pronostico(fdi: string): string | null {
    return this.estado[fdi]?.pronostico ?? null;
  }

  colorPronostico(fdi: string): string | null {
    const p = this.pronostico(fdi);
    return this.pronosticos.find((x) => x.valor === p)?.color ?? null;
  }

  labelPronostico(fdi: string): string {
    const p = this.pronostico(fdi);
    return this.pronosticos.find((x) => x.valor === p)?.label ?? "";
  }

  anchura(fdi: string, cara: CaraPerio): number | null {
    return this.estado[fdi]?.[cara]?.anchura ?? null;
  }

  colorSondaje(fdi: string, cara: CaraPerio, i: number): string | null {
    return this.configService.colorSondaje(this.sondaje(fdi, cara, i));
  }

  colorSangrado(): string {
    return this.configService.config.colores.sangrado;
  }

  get indices(): IndicesPerio {
    const universo = this.filas.reduce((acc, f) => acc.concat(f), [] as string[]);
    return calcularIndices(this.estado, this.odontogramaEstado, universo);
  }

  colorBop(v: number | null): string {
    if (v === null) return "";
    if (v < 10) return "#2e7d32";
    if (v <= 25) return "#f9a825";
    return "#e53935";
  }

  onDiente(fdi: string): void {
    if (this.mode === "readonly") return;
    this.dienteClick.emit({ fdi });
  }

}
